// constants
import * as timeSlots from "@clockwise/client-commons/src/constants/time-slot";
// internal
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
// util
import {
  DisabledWrapper,
  flexibilityExpiredTooltipCopy,
} from "#clockwise/web-commons/src/ui/disabled-wrapper";
import {
  TrialProBadgeType,
  TrialProBadgeWithTooltip,
} from "#clockwise/web-commons/src/ui/trial-pro-badge-with-tooltip";
import { TrackingEvents, useTracking } from "#clockwise/web-commons/src/util/analytics.util";
import { TrialExpirationStatus } from "#clockwise/web-commons/src/util/trial-utils";
import {
  EAutopilotWarning,
  EFlex,
  getWarningText,
  isAutopilotExplicitlyEnabled,
} from "@clockwise/client-commons/src/util/event-tag";
import {
  calculateDayOffsetTimeSlotIndex,
  convertTimeSlotToHourMinutes,
  getAutopilotTimeOfDayFlexibilitySummaryText,
  getEventMoveRangeMinMaxRange,
} from "@clockwise/client-commons/src/util/time-slot";
import { RadioGroup, RadioOption, Switch, Typography } from "@clockwise/design-system";
import {
  CheckCircleOutline,
  Email,
  ErrorOutlineOutlined,
  KeyboardArrowDown,
} from "@clockwise/design-system/icons";
import { Day, DayOffsetTimeSlot } from "@clockwise/schema";

import { OVER_EXPIRED_DAYS } from "#clockwise/web-commons/src/util/trial-constants";
import { Accordion, AccordionDetails, AccordionSummary, Button, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
// styles
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { bg_positive, bg_positive_hover } from "@clockwise/design-system/tokens";
import classnames from "classnames";
import { debounce, keys, noop, values } from "lodash";
import * as React from "react";
import { colors, greys, helpers } from "../../styles/color.styles";
import { fontFamilyBody } from "../../styles/type.styles";
import { CWIDProps } from "../../util/cw-id.util";
import { billingPageUrl, openBillingPage, pricingPageUrl } from "../../util/upgradeLinks.util";
import { CalloutCard } from "../callout-card";
import { CwIcon } from "../cw-icon";
import { ProBadge } from "../pro-badge";
import { DayCheckboxes, DayOnOffMap, defaultDayOnOffMap } from "../working-hours";
import {
  AutopilotFlexibilityEvent,
  AutopilotFlexibilityOrg,
  ExpansionPanelString,
} from "./AutopilotFlexibility.types";
import { AutopilotTimeOfDaySlider } from "./autopilot-time-of-day-slider";
import { TimeOfDaySliderTimeSlot } from "./autopilot-time-of-day-slider/AutopilotTimeOfDaySlider";
import { FlexibilityLearnMore } from "./flexibility-learn-more";

const useStyles = makeStyles({
  planRestrictedWrapper: {
    flex: "1 1 0%",
    flexDirection: "column",
  },
  panel: {
    overflow: "hidden",
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&:first-child": {
      borderTopLeftRadius: 5,
      borderTopRightRadius: 5,
    },
    "&:last-child": {
      borderBottomLeftRadius: 5,
      borderBottomRightRadius: 5,
      boxShadow: "none",
    },
    "& > .Mui-expanded": {
      minHeight: 56,
    },
  },
  panel__compact: {
    border: `1px solid ${colors.green.lighter}`,
    margin: "0 !important",

    "& > .Mui-expanded": {
      minHeight: 48,
      background: greys.white,
      borderBottom: 0,

      "&:hover": {
        background: greys.white,
      },
    },
  },
  panel__white: {
    border: `1px solid ${greys.cloudyBlue}`,
  },
  summary: {
    background: greys.white,
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    padding: "0px 12px",
    minHeight: 56,
    "& .icon": {
      fontSize: 18,
      marginRight: 6,
      color: greys.slate,
    },
    "& .titleWrapper": {
      display: "flex",
      alignItems: "baseline",
    },
    "& .title": {
      color: greys.slate,
      fontSize: "12px",
    },
    "& .badgePro": {
      marginLeft: 4,
    },
    "& .description": {
      fontSize: "14px",
      color: greys.standard,
      letterSpacing: "-.01em",
    },

    "& > .MuiExpansionPanelSummary-content.Mui-expanded": {
      margin: 0,
    },
  },
  summary__content: {
    marginRight: -6,
    alignItems: "center",
  },
  summary__compact: {
    minHeight: 48,
    background: bg_positive,
    borderBottom: 0,
    padding: "0px 15px",
    margin: 0,

    "& .title": {
      fontSize: `${14 / 16}rem`,
      lineHeight: "18px",
      fontWeight: 400,
      color: greys.slate,
      margin: 0,
    },

    "& .title__planRestricted": {
      fontSize: `${14 / 16}rem`,
      lineHeight: "18px",
      fontWeight: 400,
      color: colors.blueSky.darker,
      margin: 0,
    },

    "& .description": {
      color: greys.darkestGrey,
    },

    "& .description__planRestricted": {
      color: colors.blueSky.darker,
      fontSize: `${14 / 16}rem`,
      letterSpacing: "-.01em",
      margin: 0,
    },

    "& .description__errored": {
      color: colors.peach.darker,
    },

    // TODO: (nikita) modify pro badge directly once experiment is at 100%
    "& .badgePro": {
      marginLeft: 4,
      color: colors.blueSky.darker,
      background: helpers.opacity(colors.blueSky.lightest, 0.25),
    },

    "&:hover": {
      background: bg_positive_hover,

      "& .title": {
        color: greys.darkestGrey,
      },
    },
  },
  summary__white: {
    background: greys.white,
    borderBottom: 0,

    "&:hover": {
      background: greys.white,
    },
  },
  summary__toggle: {
    display: "flex",
    alignItems: "center",

    // wrapper around toggle
    "& > * ": {
      width: "100%",
    },

    // label and toggle
    "& > * > *": {
      display: "flex",
      justifyContent: "space-between",
    },
  },
  details__disabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
  details: {
    background: "rgba(247, 247, 247, 0.5)",
    boxShadow: "inset 0px 0px 4px rgba(0, 0, 0, 0.25)",
    padding: "8px 16px 6px",
  },
  details__compact: {
    background: greys.white,
    boxShadow: "none",
    padding: "0px 16px 6px",
    display: "flex",
    flexDirection: "column",
  },
  radio: {
    "& .Mui-checked": {
      color: colors.green.darker,
    },
  },
  radio__errored: {
    "& > span, .Mui-checked": {
      color: colors.peach.darker,
    },
  },
  warningContainer: {
    border: `solid 1px ${greys.blueGrey}`,
    borderRadius: 4,
    padding: "10px 14px",
  },
  warningContainerButton: {
    backgroundColor: colors.green.darker,
    color: "white",
    width: "100%",
    marginTop: "14px",
    display: "flex",
    "&:hover": {
      background: colors.green.lighter,
    },
    "&:disabled": {
      cursor: "unset",
      backgroundColor: greys[1],
      color: greys[2],
      borderColor: greys[2],
      justifyContent: "center",
      "&:hover": {
        backgroundColor: greys[1],
      },
    },
  },
  label: {
    fontFamily: fontFamilyBody,
    fontSize: `${14 / 16}rem`,
    lineHeight: "18px",
    fontWeight: 400,
    color: greys.darkestGrey,
    margin: 0,
  },
  label__primary__muted: {
    margin: "0px 0px 0px 4px",
    fontWeight: 700,
    color: greys.slate,
  },
  label__muted: {
    color: greys.darkGreyBlue,
  },
  flexContainer: {
    margin: "14px 0",
    borderRadius: 5,
    boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.15)",

    "& > .Mui-expanded": {
      margin: 0,
    },
  },
  flexContainer__compact: {
    margin: 0,
    boxShadow: "none",
  },
  flexContainer__extension: {
    margin: "14px 0px",
  },
  flexContainer__fixed: {
    "& > div:not(:first-child)": {
      borderTop: `solid 1px ${greys.cloudyBlue}`,
    },
  },
  flexContainer__off: {
    margin: "0px 0px 14px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginLeft: "-4px",
  },
  iconSpacing: {
    marginRight: "15px",
  },
  buttonTextSpacing: {
    marginTop: "3px",
  },
  text__peach: {
    color: colors.peach.darker,
  },
});

const AutopilotFlexibilityTrialExpired = ({
  daysLeftInTrial,
  showJoinExistingPlan,
}: {
  daysLeftInTrial: number | null;
  showJoinExistingPlan?: boolean;
}) => {
  const classes = useStyles();
  const ctaHeader =
    daysLeftInTrial && daysLeftInTrial < OVER_EXPIRED_DAYS
      ? "You are on the Free plan"
      : "Your Teams Trial has expired";
  const ctaText = showJoinExistingPlan ? "join a plan" : "upgrade";
  const ctaUrl = showJoinExistingPlan ? billingPageUrl : pricingPageUrl;

  return (
    <div
      className={`${classes.panel} ${classes.panel__white} cw-py-2 cw-px-4 cw-text-xs cw-flex cw-gap-2 cw-text-subtle cw-items-center cw-font-body`}
    >
      <span>
        <ErrorOutlineOutlined style={{ width: "14px", marginBottom: "-8px", marginRight: "4px" }} />
        {ctaHeader},{" "}
        <a className="cw-text-positive cw-underline" target="_blank" rel="noreferrer" href={ctaUrl}>
          {ctaText}
        </a>{" "}
        {showJoinExistingPlan
          ? "to mark meetings as flexible."
          : "your plan to mark meetings as flexible."}
      </span>
    </div>
  );
};

type ExpansionPanelProps = {
  fixed: boolean;
  expanded: boolean;
  onChange: () => void;
  children: React.ReactFragment;
};

const CwExpansionPanel = ({
  fixed,
  expanded,
  children,
  onChange,
  "cw-id": cwId,
}: ExpansionPanelProps & CWIDProps) => {
  const classes = useStyles();

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={onChange}
      cw-id={cwId}
      classes={{
        root: classnames(classes.panel, classes.panel__compact, {
          [classes.panel__white]: fixed,
        }),
      }}
    >
      {children}
    </Accordion>
  );
};

type CwExpansionPanelSummaryProps = {
  children: React.ReactNode;
  fixed: boolean;
};
const CwExpansionPanelSummary = ({ fixed, children }: CwExpansionPanelSummaryProps) => {
  const classes = useStyles();

  return (
    <AccordionSummary
      classes={{
        root: classnames(classes.summary, classes.summary__compact, {
          [classes.summary__white]: fixed,
        }),
        content: classes.summary__content,
      }}
      expandIcon={<KeyboardArrowDown />}
    >
      {children}
    </AccordionSummary>
  );
};

type CwExpansionPanelDetailsProps = {
  children: React.ReactNode;
  disabled: boolean;
};
const CwExpansionPanelDetails = ({ children, disabled }: CwExpansionPanelDetailsProps) => {
  const classes = useStyles();

  return (
    <AccordionDetails
      className={classnames(classes.details, classes.details__compact, {
        [classes.details__disabled]: disabled,
      })}
    >
      {children}
    </AccordionDetails>
  );
};

type PlanRestrictedDayOfWeekProps = {
  isExtension: boolean;
  onClickViewPlans: () => void;
  showJoinExistingPlan?: boolean;
};
const PlanRestrictedDayOfWeek = ({
  isExtension,
  onClickViewPlans,
  showJoinExistingPlan,
}: PlanRestrictedDayOfWeekProps) => {
  const track = useTracking();
  const onClickViewBilling = () => {
    track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_DATE_RANGE.JOIN_PLAN_CLICKED);
    openBillingPage();
  };

  const getAction = () => {
    if (showJoinExistingPlan) {
      return { text: "JOIN A PLAN", onClick: onClickViewBilling };
    } else {
      return { text: "VIEW PLANS", onClick: onClickViewPlans };
    }
  };

  return (
    <div className="cw-mb-2">
      <CalloutCard
        sentiment="pro"
        content={{
          body: (
            <div className={isExtension ? "cw-mb-3" : undefined}>
              <div className="cw-flex cw-items-center cw-mb-2">
                <p className="cw-text-13 cw-leading-normal cw-m-0">Within specific workdays</p>
                <ProBadge className="cw-text-info cw-bg-info cw-ml-1" />
              </div>
              <div className="cw-flex cw-justify-around cw-mt-2 cw-mb-2 cw-h-6">
                <DayCheckboxes
                  planRestricted
                  dayOnOffMap={defaultDayOnOffMap}
                  isValid
                  onToggleDay={noop}
                />
              </div>
              {!isExtension && (
                <p className="cw-text-13 cw-leading-normal cw-mt-2">
                  Upgrade your free plan to customize the days that Clockwise can move meetings to
                </p>
              )}
            </div>
          ),
        }}
        action={getAction()}
      />
    </div>
  );
};

type TimeRangeFlexibilityProps = {
  currentApEnabled: boolean;
  currentExpandedPanel: ExpansionPanelString;
  currentSelectedFlex: EFlex;
  showMeetingHoursConflict: boolean;
  fixed: boolean;
  disabled: boolean;
  planRestricted: boolean;
  isExtension: boolean;
  onChangeExpansion: (flex: ExpansionPanelString) => void;
  onClickViewPlans: () => void;
  onChangeFlex: (value: EFlex, daysOnOffMap: DayOnOffMap) => void;
  onChangeDaysOfWeek: (daysOnOffMap: DayOnOffMap) => void;
  showDayOfWeekToggle: boolean;
  dayOnOffMap?: DayOnOffMap;
  showJoinExistingPlan?: boolean;
};
const TimeRangeFlexibility = ({
  currentApEnabled,
  currentExpandedPanel,
  currentSelectedFlex,
  fixed,
  disabled,
  planRestricted,
  isExtension,
  showMeetingHoursConflict,
  onChangeExpansion,
  onChangeFlex,
  onClickViewPlans,
  onChangeDaysOfWeek,
  showDayOfWeekToggle,
  dayOnOffMap = {},
  showJoinExistingPlan,
}: TimeRangeFlexibilityProps) => {
  const onToggleDayOfWeek = (day: Day) => {
    const newDayOnOffMap = {
      ...dayOnOffMap,
      [day]: !dayOnOffMap[day],
    } as DayOnOffMap;

    // Handles when no days are selected (which will result in all days selected)
    if (values(newDayOnOffMap).every((value) => !value)) {
      keys(newDayOnOffMap).forEach((day) => {
        newDayOnOffMap[day as Day] = true;
      });
    }

    onChangeDaysOfWeek(newDayOnOffMap);
  };

  const COMPACT_TIME_RANGE_FLEXIBILITY_DAY_TEXT = (
    <>
      Within the{" "}
      <span className={classnames({ "cw-font-bold": currentExpandedPanel === "time-range" })}>
        day
      </span>
    </>
  );
  const COMPACT_TIME_RANGE_FLEXIBILITY_WEEK_TEXT = (
    <>
      Within the{" "}
      <span className={classnames({ "cw-font-bold": currentExpandedPanel === "time-range" })}>
        week
      </span>
    </>
  );
  const COMPACT_TIME_RANGE_FLEXIBILITY_SPECIFIC_DAY_TEXT = (
    <div className="cw-flex cw-items-center">
      Within&nbsp;
      <span
        className={classnames({
          "cw-font-bold cw-whitespace-nowrap": currentExpandedPanel === "time-range",
        })}
      >
        specific workdays
      </span>
    </div>
  );

  let compactTimeRangeCopy;
  switch (currentSelectedFlex) {
    case EFlex.Day:
      compactTimeRangeCopy = COMPACT_TIME_RANGE_FLEXIBILITY_DAY_TEXT;
      break;
    case EFlex.Week:
      compactTimeRangeCopy = COMPACT_TIME_RANGE_FLEXIBILITY_WEEK_TEXT;
      break;
    case EFlex.DayOfWeek:
      compactTimeRangeCopy = COMPACT_TIME_RANGE_FLEXIBILITY_SPECIFIC_DAY_TEXT;
      break;
  }

  const flexOptionSentiment = showMeetingHoursConflict ? "warning" : "neutral";

  return (
    <CwExpansionPanel
      fixed={!!fixed}
      expanded={currentApEnabled && currentExpandedPanel === "time-range"}
      onChange={() => onChangeExpansion("time-range")}
      cw-id="autopilot-time-range-flexibility"
    >
      <CwExpansionPanelSummary fixed={!!fixed}>
        <>
          {currentExpandedPanel !== "time-range" ? (
            <Typography className="description">{compactTimeRangeCopy}</Typography>
          ) : (
            <Typography className="title">Time range flexibility</Typography>
          )}
        </>
      </CwExpansionPanelSummary>
      <CwExpansionPanelDetails disabled={!!disabled}>
        <div>
          <RadioGroup
            value={currentSelectedFlex}
            onChange={(flex) => onChangeFlex(flex, dayOnOffMap)}
            sentiment={flexOptionSentiment}
          >
            <RadioOption
              value={EFlex.Week}
              label={
                <span className="cw-body-base">{COMPACT_TIME_RANGE_FLEXIBILITY_WEEK_TEXT}</span>
              }
              cw-id="time-range-flex-week-radio"
            />
            <RadioOption
              value={EFlex.Day}
              label={
                <span className="cw-body-base">{COMPACT_TIME_RANGE_FLEXIBILITY_DAY_TEXT}</span>
              }
              cw-id="time-range-flex-day-radio"
            />

            {showDayOfWeekToggle && !planRestricted && (
              <RadioOption
                value={EFlex.DayOfWeek}
                label={
                  <span className="cw-body-base">
                    {COMPACT_TIME_RANGE_FLEXIBILITY_SPECIFIC_DAY_TEXT}
                  </span>
                }
                cw-id="time-range-flex-day-of-week-radio"
              />
            )}
          </RadioGroup>
          {showDayOfWeekToggle &&
            (planRestricted ? (
              <PlanRestrictedDayOfWeek
                isExtension={isExtension}
                onClickViewPlans={onClickViewPlans}
                showJoinExistingPlan={showJoinExistingPlan}
              />
            ) : (
              currentSelectedFlex === EFlex.DayOfWeek && (
                <div className="cw-flex cw-ml-9 cw-mt-2 cw-mb-4 cw-h-6">
                  <DayCheckboxes
                    dayOnOffMap={dayOnOffMap}
                    isValid
                    onToggleDay={onToggleDayOfWeek}
                  />
                </div>
              )
            ))}
        </div>
        {showMeetingHoursConflict && (
          <div
            style={{
              color: colors.peach.darker,
              fontSize: 13,
              margin: "10px 0px 16px",
              lineHeight: "20px",
            }}
          >
            There's no overlap between the meeting hours of this meeting's attendees. Clockwise will
            not move this meeting.
          </div>
        )}
      </CwExpansionPanelDetails>
    </CwExpansionPanel>
  );
};

type TimeOfDayFlexibilityProps = {
  event: AutopilotFlexibilityEvent | null;
  org: AutopilotFlexibilityOrg | null;
  allowedStart?: TimeOfDaySliderTimeSlot;
  allowedEnd?: TimeOfDaySliderTimeSlot;
  selectedDate?: string;
  schedulingNewEvent?: boolean;
  isExtension?: boolean;
  currentApEnabled: boolean;
  currentExpandedPanel: ExpansionPanelString;
  showMeetingHoursConflict: boolean;
  fixed: boolean;
  disabled: boolean;
  planRestricted: boolean;
  attendeeMeetingHoursIntersect: boolean;
  onChangeExpansion: (flex: ExpansionPanelString) => void;
  onChangeTime: (
    allowedStart: TimeOfDaySliderTimeSlot,
    allowedEnd: TimeOfDaySliderTimeSlot,
  ) => void;
  showJoinExistingPlan?: boolean;
};
const TimeOfDayFlexibility = ({
  org,
  event,
  fixed,
  disabled,
  planRestricted,
  currentApEnabled,
  currentExpandedPanel,
  showMeetingHoursConflict,
  attendeeMeetingHoursIntersect,
  allowedStart,
  allowedEnd,
  selectedDate,
  schedulingNewEvent,
  isExtension,
  onChangeExpansion,
  onChangeTime,
  showJoinExistingPlan,
}: TimeOfDayFlexibilityProps) => {
  const classes = useStyles();

  const getTimeOfDayFlexibilityText = () => {
    if (!event && !org) {
      return "";
    }

    if (showMeetingHoursConflict) {
      return "No overlap between attendees' meeting hours";
    }

    const attendeeIntersectionHours = getValue(event?.suggestedAutopilotSettingsResponseV3)
      ?.intersectionOfAttendeesMeetingHours;

    const [minPossibleStart, maxPossibleEnd] = getEventMoveRangeMinMaxRange(
      attendeeIntersectionHours,
      org?.userWorkingHours?.workingHours || undefined,
      selectedDate,
    );

    const minSlot =
      (allowedStart && calculateDayOffsetTimeSlotIndex(allowedStart)) ?? minPossibleStart;

    const maxSlot = (allowedEnd && calculateDayOffsetTimeSlotIndex(allowedEnd)) ?? maxPossibleEnd;

    return getAutopilotTimeOfDayFlexibilitySummaryText(
      timeSlots.all[
        (minSlot < 0 ? minSlot + timeSlots.all.length : minSlot) % timeSlots.all.length
      ],
      timeSlots.all[maxSlot % timeSlots.all.length],
      minPossibleStart === minSlot,
      maxPossibleEnd === maxSlot,
    );
  };

  return (
    <CwExpansionPanel
      fixed={!!fixed}
      expanded={currentApEnabled && currentExpandedPanel === "time-of-day"}
      onChange={() => onChangeExpansion("time-of-day")}
      cw-id="autopilot-time-of-day-flexibility"
    >
      <CwExpansionPanelSummary fixed={!!fixed}>
        <div className="titleWrapper">
          {currentExpandedPanel !== "time-of-day" ? (
            <Typography
              className={classnames({
                description: !planRestricted,
                description__planRestricted: planRestricted,
                description__errored: showMeetingHoursConflict,
              })}
            >
              {getTimeOfDayFlexibilityText()}
              {planRestricted && attendeeMeetingHoursIntersect && <ProBadge className="badgePro" />}
            </Typography>
          ) : (
            <Typography
              className={classnames({
                title: !planRestricted,
                title__planRestricted: planRestricted,
              })}
            >
              Time of day flexibility
              {planRestricted && <ProBadge className="badgePro" />}
            </Typography>
          )}
        </div>
      </CwExpansionPanelSummary>
      <CwExpansionPanelDetails disabled={!!disabled}>
        <div className={classes.planRestrictedWrapper}>
          <AutopilotTimeOfDaySlider
            org={org}
            event={event}
            selectedDate={selectedDate}
            onChange={onChangeTime}
            allowedStart={allowedStart}
            allowedEnd={allowedEnd}
            muted={planRestricted}
            attendeeMeetingHoursIntersect={attendeeMeetingHoursIntersect}
            schedulingNewEvent={schedulingNewEvent}
            isExtension={isExtension}
            showJoinExistingPlan={showJoinExistingPlan}
          />
        </div>
      </CwExpansionPanelDetails>
    </CwExpansionPanel>
  );
};

type RoomFlexibilityProps = {
  currentApEnabled: boolean;
  currentExpandedPanel: ExpansionPanelString;
  currentAllowSimilarRooms: boolean;
  fixed: boolean;
  disabled: boolean;
  onChangeExpansion: (flex: ExpansionPanelString) => void;
  onChangeRoom: (_e: any, value: string) => void;
};
const RoomFlexibility = ({
  currentApEnabled,
  currentExpandedPanel,
  currentAllowSimilarRooms,
  fixed,
  disabled,
  onChangeExpansion,
  onChangeRoom,
}: RoomFlexibilityProps) => {
  const ROOM_FLEXIBILITY_KEEP = "Keep current room";
  const ROOM_FLEXIBILITY_ALLOW = "Ok to move to similar room";

  const roomFlexibilityCopy = currentAllowSimilarRooms
    ? ROOM_FLEXIBILITY_ALLOW
    : ROOM_FLEXIBILITY_KEEP;
  return (
    <CwExpansionPanel
      fixed={!!fixed}
      expanded={currentApEnabled && currentExpandedPanel === "room"}
      onChange={() => onChangeExpansion("room")}
      cw-id="room-flexibility"
    >
      <CwExpansionPanelSummary fixed={!!fixed}>
        <>
          <div>
            {currentExpandedPanel !== "room" ? (
              <Typography className="description">{roomFlexibilityCopy}</Typography>
            ) : (
              <Typography className="title">Room flexibility</Typography>
            )}
          </div>
        </>
      </CwExpansionPanelSummary>
      <CwExpansionPanelDetails disabled={!!disabled}>
        <RadioGroup
          value={currentAllowSimilarRooms ? "move" : "keep"}
          onChange={(value) => onChangeRoom(null, value)}
        >
          <RadioOption
            value="keep"
            label={<span className="cw-body-base">{ROOM_FLEXIBILITY_KEEP}</span>}
          />
          <RadioOption
            value="move"
            label={<span className="cw-body-base">{ROOM_FLEXIBILITY_ALLOW}</span>}
          />
        </RadioGroup>
      </CwExpansionPanelDetails>
    </CwExpansionPanel>
  );
};

type FlexibilityToggleProps = {
  isExtension: boolean;
  currentApEnabled: boolean;
  fixed: boolean;
  disabled: boolean;
  onToggle: () => void;
  trialExpirationStatus: TrialExpirationStatus | null;
  isHold: boolean;
  canUserAccessFlexMeetings: boolean;
};
const FlexibilityToggle = ({
  isExtension,
  currentApEnabled,
  fixed,
  disabled,
  onToggle,
  trialExpirationStatus,
  isHold,
  canUserAccessFlexMeetings,
}: FlexibilityToggleProps) => {
  const classes = useStyles();
  const t = React.useContext(TranslationContext);

  if (isExtension) {
    return null;
  }

  const timeLeftInTrial = trialExpirationStatus?.timeLeftInTrial || null;
  const trialIsExpiredAndNotApEnabled =
    !currentApEnabled &&
    trialExpirationStatus &&
    trialExpirationStatus.isTrialExpired &&
    !isHold &&
    !canUserAccessFlexMeetings;
  const showProBadge =
    (timeLeftInTrial !== null || trialExpirationStatus?.isTrialExpired) &&
    !fixed &&
    !isHold &&
    !canUserAccessFlexMeetings;

  const getToolTipTypeForTrialProBadge = () => {
    // if trial expired, event isn't fixed and is on, the meeting organzier is on a paid plan
    if (trialExpirationStatus?.isTrialExpired && !fixed && currentApEnabled) {
      return TrialProBadgeType.OrganizerPaidUserFree;
    } else if (trialExpirationStatus?.isTrialExpired) {
      // trial expired and meeting organizer AP paused
      return TrialProBadgeType.FlexMeetingExpired;
    } else if (currentApEnabled) {
      return TrialProBadgeType.FlexMeetingEnabled;
    } else {
      return TrialProBadgeType.FlexMeeting;
    }
  };

  const getFlexibilityLabel = () => {
    if (!currentApEnabled) {
      return "Mark as flexible";
    }
    if (fixed) {
      return "Will not flex";
    }
    return `Flexible ${isHold ? "Hold" : "Meeting"}`;
  };

  return (
    <div
      className={classnames(classes.panel, classes.panel__compact, {
        [classes.panel__white]: !currentApEnabled || fixed,
      })}
    >
      <label
        className={classnames(
          "cw-flex cw-items-center",
          "cw-min-h-[48px] cw-m-0 cw-px-4",
          "cw-group",
          {
            "cw-bg-default hover:cw-bg-default": !currentApEnabled || fixed,
            "cw-bg-positive hover:cw-bg-positive-hover": currentApEnabled && !fixed,
            "cw-cursor-pointer": !disabled,
            "cw-pointer-events-none": disabled,
          },
        )}
      >
        {fixed ? (
          <CheckCircleOutline className="cw-text-warning -cw-lm-2" />
        ) : trialIsExpiredAndNotApEnabled ? null : (
          <CwIcon
            variant="autopilot"
            color={!currentApEnabled ? "Grey" : "Green"}
            className="-cw-ml-2"
          />
        )}
        <span
          className={classnames("cw-flex-1 cw-flex cw-body-base cw-font-bold cw-ml-1", {
            "cw-text-positive": (!currentApEnabled || !fixed) && !trialIsExpiredAndNotApEnabled,
            "cw-text-warning": currentApEnabled && fixed,
            "cw-opacity-50": trialIsExpiredAndNotApEnabled && !isHold,
          })}
        >
          {getFlexibilityLabel()}
          {showProBadge ? (
            <span
              className={`cw-ml-1 ${
                trialExpirationStatus?.isTrialExpired &&
                !currentApEnabled &&
                !canUserAccessFlexMeetings
                  ? "cw-cursor-not-allowed"
                  : ""
              }`}
            >
              <TrialProBadgeWithTooltip
                showToolTip={!isHold}
                tooltipType={getToolTipTypeForTrialProBadge()}
                daysLeftOnTrial={trialExpirationStatus?.timeLeftInTrial || undefined}
                placement="bottom"
                colorOption={currentApEnabled ? "green" : "grey"}
                width={280}
              />
            </span>
          ) : null}
        </span>

        <DisabledWrapper
          isDisabled={Boolean(trialIsExpiredAndNotApEnabled)}
          tooltipBody={flexibilityExpiredTooltipCopy.body}
          tooltipHeader={flexibilityExpiredTooltipCopy.header}
          width={280}
        >
          <Tooltip
            title={disabled ? "" : t("autopilot-form:toggle-tooltip", { on: currentApEnabled })}
          >
            <span
              className={`cw-justify-self-end ${
                trialExpirationStatus?.isTrialExpired &&
                !currentApEnabled &&
                !isHold &&
                !canUserAccessFlexMeetings
                  ? "cw-opacity-50"
                  : ""
              }`}
            >
              <Switch
                cw-id="flexibility-toggle"
                checked={currentApEnabled}
                onChange={onToggle}
                disabled={disabled || Boolean(trialIsExpiredAndNotApEnabled)}
                sentiment={fixed ? "warning" : "neutral"}
              />
            </span>
          </Tooltip>
        </DisabledWrapper>
      </label>
    </div>
  );
};

const ANALYTICS_MAP: Record<Day, number> = {
  [Day.Monday]: 1,
  [Day.Tuesday]: 2,
  [Day.Wednesday]: 3,
  [Day.Thursday]: 4,
  [Day.Friday]: 5,
  [Day.Saturday]: 6,
  [Day.Sunday]: 7,
};
const dayOnOffMapToAnalyticsRepresentation = (
  dayOnOffMap: Partial<Record<Day, boolean>> | undefined,
) => {
  if (!dayOnOffMap) {
    return "";
  }

  // The Data Team requested the following representation for selected days: '12345' where 1 = Monday
  return [
    Day.Monday,
    Day.Tuesday,
    Day.Wednesday,
    Day.Thursday,
    Day.Friday,
    Day.Saturday,
    Day.Sunday,
  ]
    .map((day) => (dayOnOffMap[day] ? ANALYTICS_MAP[day] : null))
    .filter((day) => !!day)
    .join("");
};
const convertTimeSlotToDataFormat = (timeSlot: DayOffsetTimeSlot["timeSlot"]) => {
  const { hour, minute } = convertTimeSlotToHourMinutes(timeSlot);
  return `${hour <= 9 ? `0${hour}` : hour}${minute == 0 ? "00" : minute}`; // Data Team has requested the format 'HHMM'
};

export type AutopilotFlexiblitySurface =
  | "EventModal"
  | "SchedulingModal"
  | "Sidepanel"
  | "OnboardingFlexOneOnOnes1on1Dash";

export const DAY_OF_WEEK_SELECTION_DEBOUNCE_TIME = 2000;
export interface IProps {
  disabled?: boolean;
  fixed?: boolean;
  attendeeMeetingHoursIntersect: boolean;
  warning?: EAutopilotWarning;
  planRestricted: boolean;
  isAutopilotEnabled?: boolean;
  event: AutopilotFlexibilityEvent | null;
  org: AutopilotFlexibilityOrg | null;
  defaultFlex?: EFlex;
  defaultAllowSimilarRoom?: boolean;
  selectedFlex?: EFlex;
  showRoom?: boolean;
  allowSimilarRooms?: boolean;
  showJoinExistingPlan?: boolean;
  onChangeFlex: (flex: EFlex, daysOfWeek: Day[]) => void;
  onChangeRoom: (allowSimilarRoom: boolean) => void;
  onChangeTime: (
    allowedStart: TimeOfDaySliderTimeSlot,
    allowedEnd: TimeOfDaySliderTimeSlot,
  ) => void;
  onClickInvite?: (fromFlexibilityComp?: boolean) => void;
  selectedDate?: string;
  sendingInvite?: boolean;
  sentInvite?: boolean;
  allowedStart?: TimeOfDaySliderTimeSlot;
  allowedEnd?: TimeOfDaySliderTimeSlot;
  schedulingNewEvent?: boolean;
  onClickViewPlans: () => void;
  isExtension?: boolean;
  onToggle?: () => void;
  organizerForDisplay?: { name?: string; email?: string };
  showDayOfWeekToggle?: boolean;
  dayOnOffMap?: DayOnOffMap;
  surface: AutopilotFlexiblitySurface;
  onExpandSection?: (section: ExpansionPanelString) => void;
  initialExpanded?: ExpansionPanelString;
  trialExpirationStatus?: TrialExpirationStatus;
  canUserAccessFlexMeetings?: boolean;
}
export const AutopilotFlexibility = ({
  event,
  org,
  selectedDate,
  allowedStart,
  allowedEnd,
  selectedFlex,
  allowSimilarRooms,
  defaultFlex,
  defaultAllowSimilarRoom,
  onChangeFlex,
  onChangeTime,
  onChangeRoom,
  onClickInvite,
  isAutopilotEnabled,
  showJoinExistingPlan,
  disabled,
  fixed,
  showRoom,
  planRestricted,
  schedulingNewEvent,
  isExtension,
  onToggle,
  warning,
  sendingInvite,
  sentInvite,
  attendeeMeetingHoursIntersect,
  organizerForDisplay,
  showDayOfWeekToggle = false,
  dayOnOffMap,
  surface,
  onClickViewPlans,
  trialExpirationStatus,
  onExpandSection,
  initialExpanded = "",
  canUserAccessFlexMeetings = false,
}: IProps) => {
  const classes = useStyles();
  const track = useTracking();

  const roomFallback = selectedFlex !== EFlex.Pin ? defaultAllowSimilarRoom ?? false : false;
  const showMeetingHoursConflict = !attendeeMeetingHoursIntersect;

  const [expanded, setExpanded] = React.useState<ExpansionPanelString>(initialExpanded);
  const [currentAllowSimilarRooms, setCurrentAllowSimilarRooms] = React.useState(
    allowSimilarRooms ?? roomFallback,
  );
  const [currentSelectedFlex, setCurrentSelectedFlex] = React.useState(
    selectedFlex ?? defaultFlex ?? EFlex.Day,
  );
  const [currentDaysOfWeekOnOffMap, setCurrentDaysOfWeekOnOffMap] = React.useState(dayOnOffMap);
  const [previousSelectedFlex, setPreviousSelctedFlex] = React.useState(
    selectedFlex ?? defaultFlex ?? EFlex.Day,
  );
  const currentApEnabledFromProps =
    isAutopilotEnabled ?? (!!event && isAutopilotExplicitlyEnabled(event));
  const [currentApEnabled, setCurrentApEnabled] = React.useState(currentApEnabledFromProps);

  const isHold = event ? event.isHold : false;

  React.useEffect(() => {
    if (!schedulingNewEvent) {
      setCurrentSelectedFlex(selectedFlex ?? defaultFlex ?? EFlex.Day);
      setCurrentAllowSimilarRooms(allowSimilarRooms ?? defaultAllowSimilarRoom ?? false);
    }
    setCurrentApEnabled(currentApEnabledFromProps);
  }, [
    selectedFlex,
    defaultFlex,
    allowSimilarRooms,
    defaultAllowSimilarRoom,
    currentApEnabledFromProps,
    schedulingNewEvent,
  ]);

  React.useEffect(() => {
    setCurrentDaysOfWeekOnOffMap(dayOnOffMap);
  }, [dayOnOffMap]);

  const handleChangeExpansion = (_expanded: ExpansionPanelString) => {
    const newExpanded = expanded === _expanded ? "" : _expanded;
    setExpanded(newExpanded);
    onExpandSection?.(newExpanded);
  };

  React.useEffect(() => {
    // tracking for expanded flex options section
    if (expanded == "time-range") {
      const dateRange =
        selectedFlex == EFlex.DayOfWeek
          ? dayOnOffMapToAnalyticsRepresentation(dayOnOffMap)
          : selectedFlex;

      track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_DATE_RANGE.EXPAND, {
        event_id: event?.eventKey?.externalEventId,
        surface: surface,
        date_range: dateRange,
      });
    }
  }, [expanded]);

  React.useEffect(() => {
    // tracking for expanded time of day section
    if (expanded == "time-of-day") {
      track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_TIME_OF_DAY.EXPAND, {
        event_id: event?.eventKey?.externalEventId,
        surface: surface,
        time_range_start: allowedStart && convertTimeSlotToDataFormat(allowedStart?.timeSlot),
        time_range_end: allowedEnd && convertTimeSlotToDataFormat(allowedEnd?.timeSlot),
      });
    }
  }, [expanded]);

  const handleTrackingFlexChange = (selectedFlex: EFlex, daysOfWeek?: Day[]) => {
    // tracking for updated time range
    const previousDateRange =
      previousSelectedFlex == EFlex.DayOfWeek
        ? dayOnOffMapToAnalyticsRepresentation(dayOnOffMap)
        : previousSelectedFlex;

    const newDayOnOffMap: DayOnOffMap = {};
    keys(dayOnOffMap).forEach((day) => {
      newDayOnOffMap[day as Day] = false;
    });
    daysOfWeek?.forEach((day) => {
      newDayOnOffMap[day] = true;
    });

    const newDateRange =
      selectedFlex == EFlex.DayOfWeek
        ? dayOnOffMapToAnalyticsRepresentation(newDayOnOffMap)
        : selectedFlex;

    track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_DATE_RANGE.CHANGE, {
      event_id: event?.eventKey?.externalEventId,
      surface: surface,
      prev_date_range: previousDateRange,
      new_date_range: newDateRange,
    });

    setPreviousSelctedFlex(selectedFlex);
  };

  const debouncedOnChangeFlex = React.useCallback(
    debounce(onChangeFlex, DAY_OF_WEEK_SELECTION_DEBOUNCE_TIME),
    [onChangeFlex],
  );

  const handleChangeFlex = (value: EFlex, newDaysOnOffMap: DayOnOffMap) => {
    const selectedFlex = value;

    const daysOfWeekSelected = (keys(newDaysOnOffMap) as Day[]).filter(
      (day) => !!newDaysOnOffMap[day],
    );

    onChangeFlex(selectedFlex, daysOfWeekSelected);
    // Cancel any debounced calls that haven't resolved yet
    // This prevents glitchiness from spamming dayOfWeek changes and then change flex type
    debouncedOnChangeFlex.cancel();

    setCurrentSelectedFlex(selectedFlex);
    handleTrackingFlexChange(selectedFlex, daysOfWeekSelected);
  };

  const handleChangeDaysOfWeek = (newDaysOnOffMap: DayOnOffMap) => {
    const daysOfWeekSelected = (keys(newDaysOnOffMap) as Day[]).filter(
      (day) => newDaysOnOffMap[day],
    );

    // Handles batching multiple changes to daysOfWeekSelected in bursts
    setCurrentDaysOfWeekOnOffMap(newDaysOnOffMap);
    debouncedOnChangeFlex(EFlex.DayOfWeek, daysOfWeekSelected);
    handleTrackingFlexChange(EFlex.DayOfWeek, daysOfWeekSelected);
  };

  const handleTrackingTimeChange = (
    newAllowedStart: TimeOfDaySliderTimeSlot,
    newAllowedEnd: TimeOfDaySliderTimeSlot,
  ) => {
    track(TrackingEvents.FLEXIBLE_MEETING.ADJUST_TIME_OF_DAY.CHANGE, {
      event_id: event?.eventKey?.externalEventId,
      surface: surface,
      prev_time_range_start: allowedStart && convertTimeSlotToDataFormat(allowedStart?.timeSlot),
      prev_time_range_end: allowedEnd && convertTimeSlotToDataFormat(allowedEnd?.timeSlot),
      new_time_range_start:
        newAllowedStart && convertTimeSlotToDataFormat(newAllowedStart?.timeSlot),
      new_time_range_end: newAllowedEnd && convertTimeSlotToDataFormat(newAllowedEnd?.timeSlot),
    });
  };
  const handleChangeTime = (
    allowedStart: TimeOfDaySliderTimeSlot,
    allowedEnd: TimeOfDaySliderTimeSlot,
  ) => {
    handleTrackingTimeChange(allowedStart, allowedEnd);
    onChangeTime(allowedStart, allowedEnd);
  };

  const handleChangeRoom = (_e: any, value: string) => {
    const updatedAllowSimilarRooms = value === "move";
    onChangeRoom(updatedAllowSimilarRooms);
    setCurrentAllowSimilarRooms(updatedAllowSimilarRooms);
  };

  const handleToggle = () => {
    setCurrentApEnabled((prevApEnabled) => !prevApEnabled);
    onToggle?.();
  };

  const onFlexibilityFaqSelect = () => {
    track(TrackingEvents.HELP.WHAT_MEETINGS_SHOULD_MARK_FLEX);
  };

  const getInviteButtonLabelForNonUser = () => {
    if (!org || !organizerForDisplay) return "";

    let nameOnButton = "organizer";
    // Tested out the largest name you can have with the widest character "M", and 9 Ms fit in a row without breaking the button's UI
    // If the name is too long, we just user "organizer", which we know fits
    if (organizerForDisplay.name && organizerForDisplay.name.length <= 9) {
      nameOnButton = organizerForDisplay.name;
    }
    if (sentInvite) {
      return `Invite Sent!`;
    } else if (sendingInvite) {
      return "Sending...";
    } else {
      return `Invite ${nameOnButton}`;
    }
  };

  const t = React.useContext(TranslationContext);

  ///////////////
  // Rendering //
  ///////////////

  if (!isExtension && warning !== undefined) {
    return (
      <div className={classes.warningContainer}>
        <div className={classes.header}>
          <CwIcon variant="autopilot" color="Grey" />
          <p className={classnames(classes.label, classes.label__primary__muted)}>
            Cannot mark as flexible
          </p>
        </div>
        <p className={classnames(classes.label, classes.label__muted)}>
          {getWarningText(t, warning, organizerForDisplay?.email)}
        </p>
        {warning === EAutopilotWarning.NonUserOrganizer &&
          onClickInvite &&
          organizerForDisplay?.email && (
            <Button
              className={classes.warningContainerButton}
              onClick={() => {
                onClickInvite(true);
              }}
              disabled={sentInvite || sendingInvite}
            >
              {sentInvite || sendingInvite ? null : <Email className={classes.iconSpacing} />}
              <span className={classes.buttonTextSpacing}>{getInviteButtonLabelForNonUser()}</span>
            </Button>
          )}
      </div>
    );
  }

  return (
    <>
      <div
        className={classnames(classes.flexContainer, classes.flexContainer__compact, {
          [classes.flexContainer__extension]: isExtension,
          [classes.flexContainer__fixed]: fixed,
          [classes.flexContainer__off]: !currentApEnabled,
        })}
      >
        <FlexibilityToggle
          isExtension={!!isExtension}
          currentApEnabled={currentApEnabled}
          fixed={!!fixed && !isHold}
          disabled={!!disabled}
          onToggle={handleToggle}
          trialExpirationStatus={trialExpirationStatus || null}
          isHold={isHold}
          canUserAccessFlexMeetings={canUserAccessFlexMeetings}
        />
        {!currentApEnabled &&
          trialExpirationStatus?.isTrialExpired &&
          !isHold &&
          !canUserAccessFlexMeetings && (
            <AutopilotFlexibilityTrialExpired
              daysLeftInTrial={trialExpirationStatus?.timeLeftInTrial || null}
              showJoinExistingPlan={showJoinExistingPlan}
            />
          )}
        {currentApEnabled && (
          <TimeRangeFlexibility
            currentApEnabled={currentApEnabled}
            currentExpandedPanel={expanded}
            currentSelectedFlex={currentSelectedFlex}
            fixed={!!fixed && !isHold}
            disabled={!!disabled}
            isExtension={!!isExtension}
            planRestricted={planRestricted}
            showMeetingHoursConflict={showMeetingHoursConflict}
            onChangeExpansion={handleChangeExpansion}
            onChangeFlex={handleChangeFlex}
            onClickViewPlans={onClickViewPlans}
            onChangeDaysOfWeek={handleChangeDaysOfWeek}
            showDayOfWeekToggle={showDayOfWeekToggle}
            dayOnOffMap={currentDaysOfWeekOnOffMap}
            showJoinExistingPlan={showJoinExistingPlan}
          />
        )}
        {currentApEnabled && (org || event) && (
          <TimeOfDayFlexibility
            org={org}
            event={event}
            fixed={!!fixed && !isHold}
            disabled={!!disabled}
            planRestricted={planRestricted}
            currentApEnabled={currentApEnabled}
            currentExpandedPanel={expanded}
            showMeetingHoursConflict={showMeetingHoursConflict}
            attendeeMeetingHoursIntersect={attendeeMeetingHoursIntersect}
            allowedStart={allowedStart}
            allowedEnd={allowedEnd}
            selectedDate={selectedDate}
            schedulingNewEvent={schedulingNewEvent}
            isExtension={!!isExtension}
            onChangeExpansion={handleChangeExpansion}
            onChangeTime={handleChangeTime}
            showJoinExistingPlan={showJoinExistingPlan}
          />
        )}
        {currentApEnabled && showRoom && (
          <RoomFlexibility
            currentApEnabled={currentApEnabled}
            currentExpandedPanel={expanded}
            currentAllowSimilarRooms={currentAllowSimilarRooms}
            fixed={!!fixed}
            disabled={!!disabled}
            onChangeExpansion={handleChangeExpansion}
            onChangeRoom={handleChangeRoom}
          />
        )}
      </div>
      {!isExtension && !currentApEnabled && warning === undefined && (
        <FlexibilityLearnMore onFlexibilityFaqSelect={onFlexibilityFaqSelect} />
      )}
    </>
  );
};
